import { Cookies } from 'quasar';
import { isSupportedLocale } from 'src/i18n';
import { setI18nLanguage } from 'src/i18n/set-language';
import { useRouter } from 'vue-router';
import { LocaleKey } from '@/consts';
import type { ILocales } from './types';
import { addDateByDays } from 'src/utils/addDateByDays';
import { computed, useSSRContext } from 'vue';
import { useI18n } from 'vue-i18n';
export const useLang = () => {
  const defaultLocale: string = process.env.APP_DEFAULT_LOCALE || 'en';
  // BUG-1031. Three things were wrong and one was inconsistent:
  //   - `vi` carried "TH" — the flag of THAILAND — on the product's main customer segment.
  //   - `es`/`zh` were named in English ("Spanish", "China"), while `vi` used its endonym.
  //     "China" is not even a language name; it is a country.
  //   - `flag` mixed two encodings in one array: an emoji for `en`, ISO-ish codes for the
  //     rest. Nothing renders this field yet, so a consumer would have had to handle both.
  // Every entry now uses its ENDONYM (the name a speaker of that language uses) and a flag
  // emoji, one encoding. The `iso` values match `src/i18n/{en,vi,es,zh}` exactly — a spec
  // pins that, because a locale here with no message directory silently falls back.
  const availableLocales: ILocales[] = [
    {
      name: 'English',
      iso: 'en',
      flag: '🇺🇸',
    },
    {
      name: 'Tiếng Việt',
      iso: 'vi',
      flag: '🇻🇳',
    },
    {
      name: 'Español',
      iso: 'es',
      flag: '🇪🇸',
    },
    {
      name: '中文',
      iso: 'zh',
      flag: '🇨🇳',
    },
  ];
  const i18n = useI18n({ useScope: 'global' });
  const router = useRouter();
  const { t, locale } = i18n;
  const ssrContext = process.env.SERVER ? useSSRContext() : null;
  const cookies = process.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies;
  const localeCookie = computed(() => cookies.get(LocaleKey) || defaultLocale);
  const currenLocale = computed(() =>
    availableLocales.find((t) => t.iso == locale.value),
  );
  const currentLocaleIso = computed(() => currenLocale.value?.iso ?? 'en');

  const currentLangugeName = computed(() => {
    const l = availableLocales.find((t) => t.iso == locale.value);
    return l != undefined && l?.name ? l?.name : 'unknown';
  });

  // CHỈ ghi cookie. Việc lật `locale` là của `setI18nLanguage` — nó phải nạp xong message mới
  // được lật, nên không chỗ nào khác được phép gán `locale.value`.
  const setLocaleCookie = (lang: string) => {
    cookies.set(LocaleKey, lang, {
      // maxAge: 60 * 60 * 24 * 365 * 5,
      expires: addDateByDays(365),
      path: '/',
    });
  };
  // Nạp (global + bundle co-locate của route đang đứng) rồi mới lật locale. Trả `false` khi
  // locale không hợp lệ hoặc chunk lỗi → KHÔNG đổi gì, kể cả cookie: thà giữ ngôn ngữ cũ còn
  // hơn đổi nửa vời rồi kẹt chuỗi sai.
  const onSetLocale = async (lang: string) => {
    if (await setI18nLanguage(i18n, router, lang)) {
      setLocaleCookie(lang);
    }
  };
  const initialLocale = async () => {
    const lang = isSupportedLocale(localeCookie.value)
      ? localeCookie.value
      : 'en';
    if (await setI18nLanguage(i18n, router, lang)) {
      setLocaleCookie(lang);
    }
  };
  return {
    t,
    locale,
    currenLocale,
    availableLocales,
    currentLangugeName,
    onSetLocale,
    initialLocale,
    defaultLocale,
    currentLocaleIso,
  };
};
